import * as settings from '../common/settings';
import { IMapMngr, IObjectsPool } from './interfaces';
import { AspObject } from './AspObject';
import { ObjectsDrawer } from './ObjectsDrawer';
import { DisplayObject } from './DisplayObject';
import { PlotDisplayCoreObject } from './PlotDisplayCoreObject';
import { PlotCoreObject } from './PlotCoreObject';
import { type } from 'os';

const plotTemplate =
  [
    { // 0 - Symbol
      "isDrawItem": true,
      "isSpecific": true,
      "offset": { "x": 0, "y": 0, "z": 0 },
      "minSize": { "width": 3, "height": 3 },
      "alignment": { "horizontal": 0, "vertical": 0 },
      "pad": { "width": 0, "height": 0 },
      "trimSize": { "width": 0, "height": 0 },
      "name": "PlotSymbol"
    },
    { // 1 - Letter
      "isDrawItem": true,
      "isSpecific": true,
      "offset": { "x": 0, "y": 0, "z": 0 },
      "minSize": { "width": 3, "height": 3 },
      "alignment": { "horizontal": 0, "vertical": 0 },
      "pad": { "width": 0, "height": 0 },
      "trimSize": { "width": 0, "height": 0 },
      "name": "PlotLetter"
    },
    { // 2 - hook
      "isDrawItem": true,
      "isSpecific": false,
      "relatedToIndex": [1],
      "offset": { "x": 0, "y": 0 },
      "minSize": { "width": 0, "height": 0 },
      "relatedAlignment": { "horizontal": 0, "vertical": 0 },
      "alignment": { "horizontal": 0, "vertical": 0 },
      "pad": { "width": 0, "height": 0 },
      "trimSize": { "width": 0, "height": 0 },
      "name": "HookCircle"
    },
  ];

const itemsProperties = [ // elements per template
  NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha, // plot symbol color
  NeWMI.Draw.DrawMgr.Enums.EProperties.EOColor | NeWMI.Draw.DrawMgr.Enums.EProperties.EOAlpha, // plot letter color
  null, // hook circle
];

const drawFlags = [ // elements per template
  { scale: 100000, flags: [true, true, true,] },
  { scale: 500000, flags: [true, true, true] },
  { scale: Number.MAX_VALUE, flags: [true, true, true] }
];

const symbolNames = [
  'plot-uncorr',
  'plot-corr',
  'plot-iff',
  'plot-cross',
];

const miscSymbolNames = [
  'hook-circle',
];

const imagesToLoad = ObjectsDrawer.initImagesToLoad(miscSymbolNames, symbolNames);

class PlotsPerSensor {
  objects: Map<number, PlotDisplayCoreObject> = new Map();
}

class PlotsPerType {
  objects: Set<PlotDisplayCoreObject> = new Set();
}

export class PlotsCoreDrawer extends ObjectsDrawer {

  protected allPlots: Array<PlotDisplayCoreObject> = new Array();
  protected plotsPerSensor: Map<number, PlotsPerSensor> = new Map();
  protected plotsPerType: Map<number, PlotsPerType> = new Map();

  constructor(mapMngr: IMapMngr, objectsPool: IObjectsPool, parentLayer) {
    super(mapMngr, objectsPool, settings.DisplayFilter.Plots, parentLayer, plotTemplate,
      imagesToLoad, drawFlags, itemsProperties);
    // Add callback for PlotSourceHidden event
    settings.eventsEmitter.on('PlotSourceHidden', function (sensorID: number) {
      this.handleSourceHidden(sensorID);
    }.bind(this));

    // Add callback for PlotTypeHidden event
    settings.eventsEmitter.on('PlotTypeHidden', function (type: number) {
      this.handleTypeHidden(type);
    }.bind(this));

    // Add callback for PlotSourceShown event
    settings.eventsEmitter.on('PlotSourceShown', function (sensorID: number) {
      this.handleSourceShown(sensorID);
    }.bind(this));

    // Add callback for PlotTypeShown event
    settings.eventsEmitter.on('PlotTypeShown', function (type: number) {
      this.handleTypeShown(type);
    }.bind(this));
  }

  protected templateStyles(images): any[] {
    let imageStyle = new NeWMI.Draw.Styles.ImagesFontStyle({ images: images });
    // elements per template
    return [imageStyle, this.is3D() ? this.small3DTextStyle : this.smallTextStyle, imageStyle];
  }

  protected allImagesLoaded(images) {
    images['plot-uncorr'].size = { width: 7, height: 7 };
    images['plot-corr'].size = { width: 10, height: 10 };
    images['plot-iff'].size = { width: 14, height: 14 };
    images['plot-cross'].size = { width: 14, height: 14 };
    images['hook-circle'].size = { width: 25, height: 25 };
    super.allImagesLoaded(images);
  }

  protected handleSourceHidden(sensorID: number) {
    let plots = this.plotsPerSensor.get(sensorID);
    if (plots != undefined) {
      plots.objects.forEach((plot: PlotDisplayCoreObject) => {
        if (!plot.isVisible())
          this.removeObjectFromDm(plot);
      });
    }
  }

  protected handleTypeHidden(type: number) {
    let plots = this.plotsPerType.get(type);
    if (plots != undefined) {
      plots.objects.forEach((plot: PlotDisplayCoreObject) => {
        if (!plot.isVisible())
          this.removeObjectFromDm(plot);
      });
    }
  }

  protected handleSourceShown(sensorID: number) {
    if (sensorID == -1) {
      this.allPlots.forEach(plot => {
        if (plot.isVisible())
          this.addObjectToDm(plot);
      });
      return;
    }

    console.debug('Plots source shown for sensor', sensorID);
    let plots = this.plotsPerSensor.get(sensorID);
    if (plots != undefined) {
      plots.objects.forEach((plot: PlotDisplayCoreObject) => {
        if (plot.isVisible())
          this.addObjectToDm(plot);
      });
    }
  }

  protected handleTypeShown(type: number) {
    let plots = this.plotsPerType.get(type);
    if (plots != undefined) {
      plots.objects.forEach((plot: PlotDisplayCoreObject) => {
        if (plot.isVisible())
          this.addObjectToDm(plot);
      });
    }
  }

  // override ObjectsDrawer
  protected getSlotsHistory() {
    return settings.userSettings.plotTrailLength;
  }

  // override ObjectsDrawer
  protected addObject(obj: DisplayObject) {
    super.addObject(obj);

    let plot = obj as PlotDisplayCoreObject;
    this.allPlots.push(plot);

    let plots = this.plotsPerSensor.get(plot.sensorID);
    if (plots == undefined) {
      plots = new PlotsPerSensor();
      this.plotsPerSensor.set(plot.sensorID, plots);
    }
    plots.objects.set(plot.sensorPlotID, plot);

    let aspPlot = plot.getAspObject() as PlotCoreObject;
    let plotTypes = this.plotsPerType.get(aspPlot.CorrelationType);
    if (plotTypes == undefined) {
      plotTypes = new PlotsPerType();
      this.plotsPerType.set(aspPlot.CorrelationType, plotTypes);
    }
    plotTypes.objects.add(plot);

  }

  // override ObjectsDrawer
  protected deleteObject(obj: DisplayObject) {
    let plot = obj as PlotDisplayCoreObject;

    let plots = this.plotsPerSensor.get(plot.sensorID);
    if (plots != undefined) {
      plots.objects.delete(plot.sensorPlotID);
    }

    let aspPlot = plot.getAspObject() as PlotCoreObject;
    let aspplots = this.plotsPerType.get(aspPlot.CorrelationType);
    if (aspplots != undefined) {
      aspplots.objects.delete(plot);
    }

    super.deleteObject(obj);
  }

  // Override ObjectsDrawer
  createDisplayObject(obj: AspObject): DisplayObject {
    return new PlotDisplayCoreObject(obj, this);
  }

  // Override ObjectsDrawer
  getOpacity(): number {
    return settings.userSettings.plotOpacity;
  }

}
